import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import { ScriptLoaderService } from '@services/script-loader/script-loader.service';
import { UtilService } from '@services/util/util.service';

// ─── Global Window Augmentation ──────────────────────────────────────────────
declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

// ─── Public Interfaces ────────────────────────────────────────────────────────
export interface FacebookShareParams {
  href: string;
  message: string;
  method?: string;
  display?: string;
}
@Injectable({
  providedIn: 'root'
})
export class FbService {
 
  private readonly util = inject(UtilService)
  private readonly scriptLoader = inject(ScriptLoaderService);

  // ─── SDK State ─────────────────────────────────────────────────────────────
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;

  // ─── App IDs (rotated randomly to spread API rate limits) ──────────────────
  private readonly appIds = [
    '2371926249799384|0ed478f8f1b1c9ec5bf0afca3407e43e',
    '412730782638081|ae3e4947d3bef0ab1ecb3804246c1304',
    '2399457636740236|b6b0ac82abd2600e0c72b386dd61382c',
    '422205541932141|0b7a7683d8935f525c40134a5249f4b5',
    '2388821171404839|5cdc0155991ee0fb086c8ba42ffeb0e8',
    '1045930105596680|3fdc5d46a8d6e56dda75227d68482c5b',
    '637447243370103|a9220654ae8adbf1d99c90f296074c88',
    '1058901017639168|9f9e552a125e6945c07a2991c34bf606',
    '337663723602820|05e102ad2bd04e00dbbbcab86bbae92f',
    '2223471721302253|9c2d304a709435071407b85d6b07eab1',
    '817890751925466|07bf92f33f8dbd2c320b88fb04a01d72',
    '314732132540039|32f604bd5902d36b30ae4e8efe39228f',
    '363346480951851|77eb111843f44f7d259ce559b158483d',
    '350924695532240|d5250e09adcbd93b510bd026979f8192',
    '411478629648295|f87acc04f1b9c513ab4d9104331523e8'
  ];

  getFacebookID(): string {
    return this.appIds[Math.floor(Math.random() * this.appIds.length)];
  }

  /**
   * Load & initialise the Facebook JS SDK on demand (singleton).
   */
  loadFBScript(): Promise<void> {
    // Already initialised — short-circuit.
    if (this.isInitialized) {
      return Promise.resolve();
    }
    // Load already in progress — reuse the same promise.
    if (this.initPromise) {
      return this.initPromise;
    }
    // SSR guard — nothing to load server-side.
    if (!this.util.isBrowser) {
      return Promise.resolve();
    }

    this.initPromise = new Promise<void>((resolve, reject) => {
      // Must be defined BEFORE the script is inserted —
      // the FB SDK calls window.fbAsyncInit() as soon as it executes.
      window.fbAsyncInit = () => {
        try {
          window.FB.init({
            appId: environment.facebook_id,
            xfbml: false,
            version: 'v22.0'
          });
          this.isInitialized = true;
          resolve();
        } catch (err) {
          reject(err);
        }
      };
      // SDK already on window from a previous load (e.g. hot-reload).
      if (window.FB?.ui) {
        this.isInitialized = true;
        resolve();
        return;
      }
      this.scriptLoader.newLoad('facebooksdk').catch(reject);
    });
    return this.initPromise;
  }
  /** Open the Facebook share dialog. Only `href` is required. */
  async share(params: FacebookShareParams): Promise<any> {
    await this.loadFBScript();
    return new Promise((resolve, reject) => {
      if (!window.FB?.ui) {
        reject(new Error('Facebook SDK unavailable'));
        return;
      }
      window.FB.ui(
        { method: 'share', display: 'popup', ...params },
        (response: any) => {
          if (!response) {
            resolve({ cancelled: true });
            return;
          }
          if (response.error_message) {
            reject(response);
            return;
          }
          resolve(response);
        }
      );
    });
  }
}
